/**
 * LanguageSwitcher — minimal native <select> driven by the LOCALES
 * metadata from src/i18n. Writing localStorage ("ag.locale") records the
 * explicit choice; the URL prefix is swapped in the same tick so the
 * address bar always carries the SEO-canonical locale path.
 *
 * "en" navigates to the UNPREFIXED path: the root URLs are the en/x-default
 * canonicals (see scripts/generate-sitemap.mjs hreflang entries).
 *
 * Rendered inside the SettingsCrown drawer (nav overflow) so it costs
 * zero visual space on the 5-tab shell.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_LOCALE,
  LOCALES,
  LOCALE_STORAGE_KEY,
  splitLocalePrefix,
} from "../../i18n";
import { isSupportedLocale } from "../../i18n/locales";

export default function LanguageSwitcher({ onSelect }: { onSelect?: () => void }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const current = isSupportedLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE;

  function handleChange(event: React.ChangeEvent<HTMLSelectElement>) {
    const next = event.target.value;
    if (!isSupportedLocale(next) || next === current) return;
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      /* storage unavailable — the path prefix below still governs */
    }
    const { pathname, search, hash } = location;
    const stripped = splitLocalePrefix(pathname).path;
    const suffix = stripped === "/" ? "" : stripped;
    navigate(
      next === DEFAULT_LOCALE
        ? `${stripped}${search}${hash}`
        : `/${next}${suffix}${search}${hash}`,
      { replace: false },
    );
    onSelect?.();
  }

  return (
    <label className="flex items-center gap-2 w-full">
      <span className="font-caps text-micro tracking-[0.2em] uppercase text-brass-400 shrink-0">
        {t("ui.label.language")}
      </span>
      <select
        value={current}
        onChange={handleChange}
        aria-label={t("ui.label.language")}
        className="min-w-0 flex-1 bg-transparent font-body text-small text-parchment-200 border border-brass-700 rounded-[3px] px-1.5 py-1 focus:outline-none focus:border-brass-400 cursor-pointer"
      >
        {LOCALES.map((locale) => (
          <option key={locale.code} value={locale.code}>
            {locale.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}
